import { type vec2 } from 'gl-matrix';

import { type Vector3 } from '@canvas/vector-types';

import { type AnyElement, type AnyElementData } from '../all-element-types';
import {
  type ElementData,
  type Element,
  elementType,
  type ElementType,
} from '../element';

/**
 * Represents a single line segment composing a path.
 * @summary Use this in combination with other segments to form a path.
 * @description All properties of this segment are relative to the `start` of the path.
 */
export interface LineSegment {
  type: 'line';
  /**
   * The angle (in radians) this line should be drawn from
   * its starting point.
   */
  angle: number;
  /**
   * The length of the line from its starting point.
   */
  length: number;

  /**
   * @description Derived metadata for the line segment.
   */
  pathSegmentMetadata?: PathSegmentMetadata;
}

/**
 * Represents an arc.
 * @summary Use this in combination with other segments to form a path.
 * @description Model is based on {@link https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/arc}
 * @description All properties of this segment are relative to the `start` of the path.
 * @example startAngle: 0, endAngle: Math.PI * 0.5 = ╮
 * @example startAngle: Math.PI * 0.5, endAngle: Math.PI = ╯
 * @example startAngle: Math.PI, endAngle: Math.PI * 1.5 = ╰
 * @example startAngle: Math.PI * -0.5, endAngle: 0 = ╭
 */
export type ArcSegment = {
  type: 'arc';
  /**
   * @description The arc's radius. Must be positive.
   */
  radius: number;
  /**
   * @description The angle at which the arc starts in radians, measured from the positive x-axis.
   */
  startAngle: number;
  /**
   * @description The angle at which the arc ends in radians, measured from the positive x-axis.
   */
  endAngle: number;
  /**
   * @description An optional boolean value. If true, draws the arc counter-clockwise between the start and end angles. The default is false (clockwise).
   */
  counterClockwise?: boolean;
};

export type Vec2RelativeToStart = vec2;

/**
 * Defines a cubic bezier curve.
 * The start point is set externally to this segment definition.
 * In this curves local coordinate system, the start point is at 0,0
 */
export type CubicBezierSegment = {
  /**
   * @description the type of this segment
   */
  type: 'cubic-bezier';
  /**
   * @description the control point for the start of the curve
   * relative to the start point
   */
  control1: Vec2RelativeToStart;
  /**
   * @description the control point for the end of the curve
   * relative to the start point
   */
  control2: Vec2RelativeToStart;
  /**
   * @description the end of the curve
   * relative to the start point
   */
  end: Vec2RelativeToStart;

  /**
   * @description Derived metadata for the cubic bezier segment.
   */
  pathSegmentMetadata?: PathSegmentMetadata;
};

export type Segment = LineSegment | ArcSegment | CubicBezierSegment;
export type SegmentType = Segment['type'];

/**
 * A number between 0 and 1 relative to a distance.
 * @description Used to position anchors relative to a width (left) or height (top).
 */
export type Proportion = number;

/**
 * Where a path is anchored to an element.
 */
export type ElementAnchor = {
  /**
   * A value between 0 and 1 representing the percentange distance from the left of the element's
   * bounding box where the anchor is placed.
   * @description The bounding box is the rectangular bounding box.
   */
  left: Proportion;
  /**
   * A value between 0 and 1 representing the percentange distance from the top of the element's
   * bounding box where the anchor is placed.
   * @description The bounding box is the rectangular bounding box.
   */
  top: Proportion;
};

/**
 * The mode of a path. Affects the following behaviours in the Canvas app:
 * - hit testing
 * - rendering
 */
export enum PathPresentation {
  STRAIGHT = 1,
  DYNAMIC = 2,
  CURVED = 3,
}

export const presentationToString = (presentation: PathPresentation) => {
  return PathPresentation[presentation];
};

export const capToString = (cap: PathCap | undefined) => {
  if (cap === undefined) {
    return PathCap[PathCap.NONE];
  }
  return PathCap[cap];
};

export enum PathPresentationMode {
  SMOOTH_SERIES = 1,
  BALANCED_CURVATURE = 2,
}

export enum PathCap {
  NONE = 1,
  ARROW = 2,
  FILLED_ARROW = 3,
  OPEN_ARROW = 4,
  FILLED_DIAMOND = 5,
  OPEN_DIAMOND = 6,
  OPEN_CIRCLE = 7,
  SLASH = 8,
  TRIPLE_BAR = 9,
  OPEN_CIRCLE_CROSS = 10,
  CROSS = 11,
  CROSS_CROWS_FOOT = 12,
  CROWS_FOOT = 13,
  CIRCLE_CROWS_FOOT = 14,
}

export enum PathStroke {
  SMALL = 1,
  MEDIUM = 2,
  LARGE = 3,
  /**
   * Used with the specific purpose of calculating bounds without worrying about
   * line thicknesses
   */
  NONE = 0,
}

export enum PathStrokeStyle {
  SOLID = 1,
  DASHED = 2,
  DOTTED = 3,
}

export interface PathElementData extends ElementData<ElementType> {
  /**
   * The list of segments which compose this path.
   */
  segments: Segment[];
  /**
   * Where this path starts.
   * @description The start position will be derived based on a connected element if {@link sourceElement} is set.
   * @description The start position will be kept in sync with a {@link sourceElement} if it exists.
   */
  start: vec2;
  /**
   * Where this path ends.
   * @description The end position will be derived based on a connected element if {@link targetElement} is set.
   * @description The end position will be kept in sync with a {@link targetElement} if it exists.
   */
  end: vec2;
  /**
   * The presentation mode of a path.
   * @description Straight paths are rendered with straight lines.
   * @description Dynamic paths are rendered with a curve-finding algorithm in Canvas.
   * @description Curved paths are rendered using an algorithm producing a series of bezier curves.
   */
  presentation: PathPresentation;
  /**
   * Indicates the cap rendered at the start of the path
   */
  startCap?: PathCap;
  /**
   * Indicates the cap rendered at the end of the path
   */
  endCap?: PathCap;
  /**
   * The colour of this path.
   */
  color: Vector3;
  /**
   * The stroke width of the path.
   */
  stroke: PathStroke;
  /**
   * The stroke style of the path.
   */
  strokeStyle?: PathStrokeStyle;
}

/**
 * @description Derived metadata for general attributes of a path segment.
 */
export type PathSegmentMetadata = {
  /**
   * @description Informs how we create and place path handles on the path segment
   */
  pathHandleMetadata?: PathHandleMetadata;
  /**
   * @description Informs whether the path segment should be rendered (undefined resolves as true)
   */
  isVisible?: boolean;
};

/**
 * a string containing data to inform how we create and place path handles
 * each value is either "id:<waypointId>" (e.g. "id:foo"), "generated" or "no-handle"
 *
 * - "id:<waypointId>" is when the line exists because of a waypoint passed into the function
 * - "generated" is when the path finding algorithm needed to insert this line itself
 * - "no-handle" is like "generated" but the line should not have a ghost handle shown on it
 */
export type PathHandleMetadata = `generated` | 'no-handle' | `id:${string}`;

export interface PathElement extends PathElementData, Element<ElementType> {}

export function isPath(element: AnyElement | undefined): element is PathElement {
  return element?.type === pathElementType;
}

export function isPathData(
  element: AnyElementData | undefined,
): element is PathElementData {
  return element?.type === pathElementType;
}

export const strokeToThickness: Record<PathStroke, number> = {
  [PathStroke.NONE]: 0,
  [PathStroke.SMALL]: 2,
  [PathStroke.MEDIUM]: 4,
  [PathStroke.LARGE]: 6,
};

export const strokeToArrowheadSizeMultiplier: Record<PathStroke, number> = {
  [PathStroke.NONE]: 1,
  [PathStroke.SMALL]: 1,
  [PathStroke.MEDIUM]: 1.2,
  [PathStroke.LARGE]: 1.6,
};

export const pathElementType = elementType('path');

/**
 * Converts a PathCap enum value to a boolean. We use this to avoid pulling in
 * `@canvas/model` into `@canvas/path`, when all it wants to know is if a cap is
 * currently active or not.
 * @param cap - The PathCap enum value to convert to a boolean.
 * @returns True if the cap is an arrow that is not blank, false otherwise.
 */
export const pathCapToBoolean = (cap: PathCap | undefined): boolean => {
  return Boolean(cap && cap !== PathCap.NONE);
};

/**
 * The position of path decorations relative to the path.
 */
export enum PathOffsetPosition {
  CENTRE = 0, // Centre of the path
  LEFT = 1, // Left of the path according to path direction
  RIGHT = 2, // Right of the path according to path direction
}

export function pathOffsetPositionToDistance(
  position: PathOffsetPosition,
  distanceFromPath: number,
): number {
  switch (position) {
    case PathOffsetPosition.LEFT:
      return 1 * distanceFromPath;
    case PathOffsetPosition.CENTRE:
      return 0;
    case PathOffsetPosition.RIGHT:
      return -1 * distanceFromPath;
  }
}

export function distanceToPathOffsetPosition(distance: number): PathOffsetPosition {
  if (distance > 0) {
    return PathOffsetPosition.LEFT;
  } else if (distance < 0) {
    return PathOffsetPosition.RIGHT;
  }
  return PathOffsetPosition.CENTRE;
}
